"use client";

import styles from "./VerificationEmailVerifyStep.module.scss";
import {
  useEffect,
  useRef,
  useState } from "react";
import { useTranslations } from "@/i18n";

import { useAuth, useAuthStore } from "@/hooks";
import { setCurrentStep } from "@/stores";
import {
  sendAccountEmailVerificationCodeRequest,
  verifyAccountEmailCodeRequest,
} from "../../_helpers";
import {
  Button,
  Flex,
  Fieldset,
  InputOtp,
} from "@vetty/ui";

export const VerificationEmailVerifyStep = () => {
  const t = useTranslations("verification.emailVerify");
  const { user } = useAuth();
  const { refreshAuth } = useAuthStore();
  const [code, setCode] = useState("");
  const [otpKey, setOtpKey] = useState(0);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const verifyInFlight = useRef(false);

  const emailVerified = Boolean(user?.emailVerified);

  useEffect(() => {
    if (emailVerified) {
      setCurrentStep(2);
    }
  }, [emailVerified]);

  const verifyCode = async (trimmed: string) => {
    if (verifyInFlight.current) return;
    if (!/^\d{6}$/.test(trimmed)) {
      setError(t("invalidCode"));
      return;
    }
    verifyInFlight.current = true;
    setLoading(true);
    setError("");
    try {
      await verifyAccountEmailCodeRequest(trimmed);
      await refreshAuth();
      setCurrentStep(2);
    } catch {
      setError(t("verifyFailed"));
      setOtpKey((k) => k + 1);
      setCode("");
    } finally {
      setLoading(false);
      verifyInFlight.current = false;
    }
  };

  const handleResend = async () => {
    setSending(true);
    setError("");
    try {
      await sendAccountEmailVerificationCodeRequest();
      setOtpKey((k) => k + 1);
      setCode("");
    } catch {
      setError(t("sendFailed"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Flex direction={"column"} className={styles["comp"]}>
      <Flex className={styles["comp__signing-as"]} gap={5}>
        <div>{t("signingAsLabel")}</div>
        <strong>{user?.email ?? ""}</strong>
      </Flex>

      <p className={styles["comp__sub-note"]}>{t("noteInbox")}</p>

      <Flex direction={"column"} gap={24}>
        <Fieldset isError={!!error} errorMessage={error}>
          <Flex direction={"column"} gap={8}>
            <InputOtp
              key={otpKey}
              disabled={loading}
              label={t("codeLabel")}
              hasError={!!error}
              onChange={(v) => {
                setCode(v);
                setError("");
              }}
              onSubmit={(full) => {
                void verifyCode(full);
              }}
            />
          </Flex>
        </Fieldset>

        <Flex gap={12} className={styles["comp__actions"]}>
          <Button
            variant="secondary"
            loading={sending}
            onClick={handleResend}
            className={styles["comp__submit-button"]}
          >
            {t("resend")}
          </Button>

          <Button
            loading={loading}
            disabled={loading || code.length !== 6}
            onClick={() => void verifyCode(code)}
            className={styles["comp__submit-button"]}
          >
            {t("continue")}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};
